import { ChangeEvent } from 'react'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import { EUI_FORM_MAX_WIDTH } from '../euiParity'
import useReasonOptions from './useReasonOptions'
import { ManualAdjustmentReasonType, ReasonPickerProps } from './types'

/**
 * `EuiSelect` is a native `<select>`; MUI's equivalent is a `TextField` in
 * select mode, which renders a button plus a popover listbox. We take MUI's
 * own presentation rather than forcing `SelectProps={{ native: true }}` to
 * mimic EUI's markup — the tradeoff is that option queries differ by branch,
 * so the specs cover each branch separately.
 *
 * `label`/`error`/`helperText` arrive from `FormRowMui`'s cloneElement (the
 * floating label lives inside the control), so they are forwarded to the
 * `TextField` rather than ignored. `isInvalid` is the consumer's own flag and
 * is OR-ed with them.
 *
 * Loading diverges on purpose. `EuiSelect`'s `isLoading` overlays a spinner and
 * leaves the control usable over an empty option list; MUI has no equivalent on
 * a select, so we disable it while the query is in flight. That is MUI's own
 * idiom and we don't reproduce EUI's, per the standing rule — both branches
 * still make an unselectable control out of an empty list, so nothing a user
 * can do differs.
 */
export default function ReasonPickerMui( {
	selectedReason,
	onChange,
	reasonType,
	filterOptions,
	label,
	error,
	helperText,
	isInvalid,
	disabled,
	fullWidth,
	id,
}: ReasonPickerProps ) {
	const { options, reasons, loading } = useReasonOptions( { reasonType, filterOptions } )

	const handleChange = ( event: ChangeEvent<HTMLInputElement> ) => {
		const reasonStr = event.target.value as ManualAdjustmentReasonType
		const reasonObj = reasons.find( ( reason ) => reason?.reason === reasonStr )
		if ( reasonObj ) {
			onChange( reasonStr, reasonObj )
		}
	}

	return (
		<TextField
			select
			// EUI form controls fill their container capped at 400px, and
			// `fullWidth` lifts the cap; mirror that rather than collapsing to
			// content width (see the same treatment in the combo boxes).
			fullWidth
			sx={{ maxWidth: fullWidth ? '100%' : EUI_FORM_MAX_WIDTH }}
			// `hasNoInitialSelection` on the EUI branch — an empty value means
			// nothing is chosen yet. Also empty while the selection isn't among the
			// current options (loading, or filtered out when AdjustQuantity flips
			// direction): MUI warns on an out-of-range value, native `<select>` doesn't.
			value={options.some( ( option ) => option.value === selectedReason ) ? selectedReason : ''}
			onChange={handleChange}
			label={label}
			// A real OR, not `??`: `isInvalid` comes from the consumer and `error`
			// from FormRow's clone, and either alone means invalid. Same call as
			// `toMuiComboBoxProps` (sc-567878).
			error={Boolean( isInvalid ) || Boolean( error )}
			helperText={helperText}
			disabled={loading || Boolean( disabled )}
			id={id}
		>
			{options.map( ( option ) => (
				<MenuItem key={String( option.value )} value={String( option.value )}>
					{option.text}
				</MenuItem>
			) )}
		</TextField>
	)
}
